import React from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {Chips} from "primereact/chips";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import {AppContext} from "../../HomePage";

// Shared Site Rule editor (unblocker capability routing): OR-groups of required caps, a JS-render
// flag, and a per-host concurrency cap. Controlled component — the parent owns `target` (the rule
// being edited; null = closed) and `setTarget` (its state setter; supports functional updates);
// `onSave` persists it. Used by both the Proxy Monitor and the Canary screens so the two stay in
// sync. When `target.existing` is true the host is fixed (dropdown disabled).
export function SiteRuleDialog({target, setTarget, onSave, capabilityOptions = [], siteOptions = []}) {
    const {t} = React.useContext(AppContext);
    return (
        <Dialog header={t('proxy_monitor.site_rule')} visible={target != null}
                style={{maxWidth: '40vw', minWidth: '24vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                modal={true} onHide={() => setTarget(null)}
                footer={<div>
                    <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-warning"
                            onClick={() => setTarget(null)}/>
                    <Button label={t('actions.save')} icon="pi pi-check" className="p-button-success"
                            onClick={onSave}
                            disabled={!(target && (target.host || '').trim())}/>
                </div>}>
            {target &&
                <div className="p-fluid" style={{display: 'flex', flexDirection: 'column', gap: '0.9em'}}>
                    <div>
                        <label>{t('proxy_monitor.host')}</label>
                        <Dropdown value={target.host} options={siteOptions} filter={true} showClear={true}
                                  disabled={target.existing}
                                  emptyMessage={t('proxy_monitor.no_sites')} emptyFilterMessage={t('proxy_monitor.no_sites')}
                                  onChange={(e) => setTarget({...target, host: e.value})}
                                  placeholder={t('proxy_monitor.select_sites')} style={{width: '100%'}}/>
                    </div>
                    <div>
                        <label>{t('proxy_monitor.requires')}</label>
                        <small style={{color: 'var(--text-color-secondary)', margin: '2px 0 8px', display: 'block'}}>
                            {t('proxy_monitor.requires_groups_hint')}</small>
                        {(target.groups || [[]]).map((g, gi) =>
                            <div key={gi}>
                                {gi > 0 &&
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', margin: '7px 0'}}>
                                        <div style={{flex: 1, height: 1, background: 'var(--surface-border)'}}/>
                                        <span style={{fontSize: '0.72em', fontWeight: 700, color: 'var(--text-color-secondary)',
                                                      border: '1px solid var(--surface-border)', borderRadius: '10px', padding: '1px 9px'}}>
                                            {t('proxy_monitor.or')}</span>
                                        <div style={{flex: 1, height: 1, background: 'var(--surface-border)'}}/>
                                    </div>}
                                <div style={{display: 'flex', alignItems: 'flex-start', gap: '6px', borderRadius: '6px', padding: '6px',
                                             border: '1px solid ' + ((target.activeGroup || 0) === gi ? 'rgba(34,197,94,0.45)' : 'var(--surface-border)')}}>
                                    <Chips value={g} separator="," style={{flex: 1, display: 'block'}}
                                           placeholder={t('proxy_monitor.add_capability')}
                                           onFocus={() => setTarget(s => ({...s, activeGroup: gi}))}
                                           onChange={(e) => setTarget(s => ({...s, activeGroup: gi,
                                               groups: (s.groups || [[]]).map((x, i) => i === gi ? e.value : x)}))}/>
                                    {(target.groups || []).length > 1 &&
                                        <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                                                tooltip={t('proxy_monitor.remove_group')}
                                                onClick={() => setTarget(s => {
                                                    const groups = (s.groups || []).filter((_, i) => i !== gi);
                                                    return {...s, groups: groups.length ? groups : [[]],
                                                            activeGroup: Math.max(0, Math.min(s.activeGroup || 0, groups.length - 1))};
                                                })}/>}
                                </div>
                            </div>)}
                        <Button label={t('proxy_monitor.add_or_group')} icon="pi pi-plus"
                                className="p-button-text p-button-sm" style={{marginTop: '4px'}}
                                onClick={() => setTarget(s => {
                                    const groups = [...(s.groups || [[]]), []];
                                    return {...s, groups, activeGroup: groups.length - 1};
                                })}/>
                        <small style={{color: 'var(--text-color-secondary)', marginTop: '2px', display: 'block'}}>
                            {t('proxy_monitor.requires_hint')}</small>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.6em'}}>
                        <InputSwitch checked={!!target.requires_render}
                                     onChange={(e) => setTarget({...target, requires_render: e.value})}/>
                        <div>
                            <label style={{margin: 0}}>Render (JS)</label>
                            <small style={{color: 'var(--text-color-secondary)', display: 'block'}}>
                                Force this site through the JS-render tier (headless browser), not cheap-fetch.</small>
                        </div>
                    </div>
                    <div>
                        <label>{t('proxy_monitor.max_concurrency')}</label>
                        <InputNumber value={target.max_concurrency || 0} min={0} max={100} showButtons
                                     onValueChange={(e) => setTarget({...target, max_concurrency: e.value})}
                                     style={{width: '100%'}} inputStyle={{width: '100%'}}/>
                        <small style={{color: 'var(--text-color-secondary)', marginTop: '4px', display: 'block'}}>
                            {t('proxy_monitor.max_concurrency_hint')}</small>
                    </div>
                    {capabilityOptions.length > 0 &&
                        <div>
                            <div style={{fontSize: '0.85em', color: 'var(--text-color-secondary)', marginBottom: '5px'}}>
                                {t('proxy_monitor.existing')} · {t('proxy_monitor.add_to_active_group')}:</div>
                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
                                {capabilityOptions.map(c => {
                                    const gi = target.activeGroup || 0;
                                    const on = ((target.groups || [[]])[gi] || []).includes(c);
                                    // onMouseDown + preventDefault: toggle without stealing focus from the Chips
                                    // input, so clicking a tag works even mid-typing. Adds to / removes from the
                                    // ACTIVE group (the one last focused).
                                    return <span key={c}
                                                 onMouseDown={(e) => {
                                                     e.preventDefault();
                                                     setTarget(s => {
                                                         const idx = s.activeGroup || 0;
                                                         const groups = (s.groups || [[]]).map(x => [...x]);
                                                         const cur = groups[idx] || (groups[idx] = []);
                                                         groups[idx] = on ? cur.filter(x => x !== c) : [...cur, c];
                                                         return {...s, groups};
                                                     });
                                                 }}
                                                 style={{cursor: 'pointer', fontSize: '0.8em', borderRadius: '12px', padding: '2px 10px',
                                                         whiteSpace: 'nowrap', userSelect: 'none',
                                                         border: '1px solid ' + (on ? 'rgba(34,197,94,0.5)' : 'var(--surface-border)'),
                                                         background: on ? 'rgba(34,197,94,0.14)' : 'transparent',
                                                         color: on ? '#16a34a' : 'var(--text-color-secondary)'}}>
                                        {on ? '✓ ' : '+ '}{c}</span>;
                                })}
                            </div>
                        </div>}
                    <small style={{color: 'var(--text-color-secondary)'}}>{t('proxy_monitor.site_rule_hint')}</small>
                </div>}
        </Dialog>
    );
}
